import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, Phone, Eye, Lock, Users, Flag, ExternalLink } from 'lucide-react';
import { PageShell } from './layout/PageShell';

const SafetyCenter = () => {
  const navigate = useNavigate();

  return (
    <PageShell>
      {/* Header */}
      <nav className="surface-raised sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-slate-600 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            
            <div className="flex items-center space-x-2">
              <Shield className="w-6 h-6 text-rose-600" />
              <span className="font-playfair text-xl font-bold text-slate-900 dark:text-slate-50">
                Safety & Support
              </span>
            </div>

            <div className="w-20"></div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="glass rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h1 className="font-playfair text-4xl font-bold text-slate-900 dark:text-slate-50 mb-4">
              Safety & Support Resources
            </h1>
            <p className="text-xl text-slate-600 dark:text-slate-300">
              Tools to protect yourself and trusted external resources for support.
            </p>
          </div>

          <div className="space-y-8">
            {/* Emergency Resources - External */}
            <section className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800/50 p-6 rounded-lg">
              <div className="flex items-center space-x-3 mb-4">
                <Phone className="w-6 h-6 text-red-600 dark:text-red-400" />
                <h2 className="font-playfair text-2xl font-semibold text-red-900 dark:text-red-200">
                  Emergency Resources
                </h2>
              </div>
              
              <p className="text-red-800 dark:text-red-300 mb-4">
                For urgent or crisis situations, these trusted external organizations provide immediate support:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-red-900 dark:text-red-200 mb-3">Immediate Danger</h3>
                  <div className="space-y-2 text-red-800 dark:text-red-300">
                    <div>🚨 <strong>Emergency Services:</strong> 911</div>
                    <div className="flex items-center gap-1">
                      📞 <strong>National Domestic Violence Hotline:</strong>{' '}
                      <a href="tel:1-800-799-7233" className="underline">1-800-799-7233</a>
                      <ExternalLink className="w-3 h-3" />
                    </div>
                    <div>🔗 <strong>Crisis Text Line:</strong> Text HOME to 741741</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-red-900 dark:text-red-200 mb-3">Sexual Assault Resources</h3>
                  <div className="space-y-2 text-red-800 dark:text-red-300">
                    <div className="flex items-center gap-1">
                      🆘 <strong>RAINN National Hotline:</strong>{' '}
                      <a href="tel:1-800-656-4673" className="underline">1-800-656-4673</a>
                      <ExternalLink className="w-3 h-3" />
                    </div>
                    <div className="flex items-center gap-1">
                      💬 <strong>Online Chat:</strong>{' '}
                      <a href="https://www.rainn.org" target="_blank" rel="noopener noreferrer" className="underline">rainn.org</a>
                      <ExternalLink className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-red-700 dark:text-red-400 text-sm mt-4 italic">
                These resources operate independently and are available regardless of your Unity Love account status.
              </p>
            </section>

            {/* On-Platform Safety Tools */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <Lock className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                <h2 className="font-playfair text-2xl font-semibold text-slate-900 dark:text-slate-50">
                  On-Platform Safety Tools
                </h2>
              </div>
              
              <p className="text-slate-600 dark:text-slate-300 mb-6">
                You have direct control over your safety on Unity Love:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <Eye className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">Profile Verification</h3>
                  </div>
                  <ul className="text-slate-700 dark:text-slate-300 space-y-1">
                    <li>• Photo verification system</li>
                    <li>• Age verification (18+ only)</li>
                    <li>• Identity confirmation via Stripe</li>
                    <li>• Verified badge for confirmed profiles</li>
                  </ul>
                </div>
                
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <Flag className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">Reporting & Blocking</h3>
                  </div>
                  <ul className="text-slate-700 dark:text-slate-300 space-y-1">
                    <li>• Report profiles that violate guidelines</li>
                    <li>• Block users instantly</li>
                    <li>• Report inappropriate messages</li>
                    <li>• Include screenshots for context</li>
                  </ul>
                </div>
                
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">Content Moderation</h3>
                  </div>
                  <ul className="text-slate-700 dark:text-slate-300 space-y-1">
                    <li>• AI-powered content screening</li>
                    <li>• Community guidelines enforcement</li>
                    <li>• Reports reviewed by our team according to community guidelines</li>
                    <li>• Accounts suspended for violations</li>
                  </ul>
                </div>
                
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <Shield className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">Privacy Controls</h3>
                  </div>
                  <ul className="text-slate-700 dark:text-slate-300 space-y-1">
                    <li>• Control who can message you</li>
                    <li>• Profile visibility settings</li>
                    <li>• Secure messaging</li>
                    <li>• Data protection measures</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Online Safety Tips */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
                Online Safety Best Practices
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-green-700 dark:text-green-400">✅ Do This</h3>
                  <div className="space-y-3">
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1">Protect Personal Information</h4>
                      <p className="text-slate-700 dark:text-slate-300 text-sm">Don&apos;t share your last name, address, workplace, or financial information early in conversations.</p>
                    </div>
                    
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1">Trust Your Instincts</h4>
                      <p className="text-slate-700 dark:text-slate-300 text-sm">If something feels wrong or too good to be true, trust that feeling and proceed with caution.</p>
                    </div>
                    
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1">Video Chat First</h4>
                      <p className="text-slate-700 dark:text-slate-300 text-sm">Have a video conversation before meeting to verify identity and build comfort.</p>
                    </div>
                    
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1">Use Platform Messaging</h4>
                      <p className="text-slate-700 dark:text-slate-300 text-sm">Keep conversations on Unity Love initially rather than moving to external platforms.</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-red-700 dark:text-red-400">❌ Avoid This</h3>
                  <div className="space-y-3">
                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1">Don&apos;t Send Money</h4>
                      <p className="text-slate-700 dark:text-slate-300 text-sm">Never send money, gift cards, or financial assistance to someone you haven&apos;t met in person.</p>
                    </div>
                    
                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1">Don&apos;t Share Intimate Photos</h4>
                      <p className="text-slate-700 dark:text-slate-300 text-sm">Avoid sending intimate or compromising photos that could be misused.</p>
                    </div>
                    
                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1">Don&apos;t Ignore Red Flags</h4>
                      <p className="text-slate-700 dark:text-slate-300 text-sm">Pay attention to pressuring behavior, inconsistent stories, or requests for personal information.</p>
                    </div>
                    
                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1">Don&apos;t Meet Privately Initially</h4>
                      <p className="text-slate-700 dark:text-slate-300 text-sm">Always meet in public places for first meetings and tell someone where you&apos;re going.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Meeting Safety */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
                In-Person Meeting Safety
              </h2>
              
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 p-6 rounded-lg mb-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">Important Reminder</h3>
                    <p className="text-slate-700 dark:text-slate-300">
                      Unity Love facilitates online connections, but we cannot guarantee the safety of offline meetings. You are responsible for your personal safety when meeting in person.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Before Meeting</h3>
                  <ul className="text-slate-700 dark:text-slate-300 text-sm space-y-1">
                    <li>• Verify identity through video chat</li>
                    <li>• Tell a trusted friend your plans</li>
                    <li>• Share meeting location and time</li>
                    <li>• Plan your own transportation</li>
                    <li>• Meet in a public, busy location</li>
                  </ul>
                </div>
                
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">During the Meeting</h3>
                  <ul className="text-slate-700 dark:text-slate-300 text-sm space-y-1">
                    <li>• Stay in public areas</li>
                    <li>• Keep your phone charged and accessible</li>
                    <li>• Don&apos;t leave drinks unattended</li>
                    <li>• Trust your instincts about comfort</li>
                    <li>• Have an exit strategy ready</li>
                  </ul>
                </div>
                
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Red Flags to Watch</h3>
                  <ul className="text-slate-700 dark:text-slate-300 text-sm space-y-1">
                    <li>• Pressure to meet privately</li>
                    <li>• Asking for money or favors</li>
                    <li>• Aggressive or controlling behavior</li>
                    <li>• Substance abuse during meeting</li>
                    <li>• Disrespecting boundaries</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Reporting Process */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
                How to Report Safety Concerns
              </h2>
              
              <div className="space-y-4">
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/50 p-4 rounded-lg">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Immediate Safety Concerns</h3>
                  <p className="text-slate-700 dark:text-slate-300 mb-3">If you&apos;re in immediate danger:</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ul className="text-slate-700 dark:text-slate-300 space-y-1">
                      <li>• <strong>Call 911</strong> for emergencies</li>
                      <li>• Contact local law enforcement</li>
                      <li>• Remove yourself from dangerous situations</li>
                    </ul>
                    <ul className="text-slate-700 dark:text-slate-300 space-y-1">
                      <li>• Block the user on Unity Love</li>
                      <li>• Document evidence (screenshots)</li>
                      <li>• Report via our in-app reporting tools</li>
                    </ul>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Report on Platform</h3>
                    <ul className="text-slate-700 dark:text-slate-300 space-y-2">
                      <li>• Use the report button on any profile</li>
                      <li>• Report inappropriate messages</li>
                      <li>• Block users who make you uncomfortable</li>
                      <li>• Include context and screenshots when reporting</li>
                    </ul>
                  </div>
                  
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Contact Unity Love</h3>
                    <ul className="text-slate-700 dark:text-slate-300 space-y-2">
                      <li>📧 <strong>General Support:</strong> support@joinunitylove.com</li>
                      <li>🔐 <strong>Privacy Concerns:</strong> privacy@joinunitylove.com</li>
                    </ul>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mt-3">
                      We review all reports but cannot provide real-time emergency response. For emergencies, contact 911.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* External Safety Resources */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
                Trusted External Resources
              </h2>
              
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                These organizations provide professional support and operate independently of Unity Love:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Relationship Safety</h3>
                  <ul className="text-slate-700 dark:text-slate-300 space-y-2">
                    <li className="flex items-center gap-1">
                      • <a href="https://www.thehotline.org" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">National Domestic Violence Hotline</a>
                      <ExternalLink className="w-3 h-3 text-indigo-400" />
                    </li>
                    <li className="flex items-center gap-1">
                      • <a href="https://www.rainn.org" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">RAINN (Sexual Assault Resources)</a>
                      <ExternalLink className="w-3 h-3 text-indigo-400" />
                    </li>
                    <li className="flex items-center gap-1">
                      • <a href="https://www.crisistextline.org" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">Crisis Text Line</a>
                      <ExternalLink className="w-3 h-3 text-indigo-400" />
                    </li>
                    <li className="text-slate-700 dark:text-slate-300">• Local women&apos;s shelters and crisis centers</li>
                  </ul>
                </div>
                
                <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Mental Health Support</h3>
                  <ul className="text-slate-700 dark:text-slate-300 space-y-2">
                    <li className="flex items-center gap-1">
                      • <a href="https://www.samhsa.gov" target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:underline">SAMHSA National Helpline</a>
                      <ExternalLink className="w-3 h-3 text-teal-400" />
                    </li>
                    <li className="flex items-center gap-1">
                      • <a href="https://988lifeline.org" target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:underline">988 Suicide &amp; Crisis Lifeline</a>
                      <ExternalLink className="w-3 h-3 text-teal-400" />
                    </li>
                    <li>• Local therapy and counseling services</li>
                    <li>• Community mental health centers</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Closing Statement */}
            <div className="text-center pt-6 border-t border-slate-200 dark:border-slate-700 mt-8">
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                Your safety matters. We&apos;re committed to improving these tools as Unity Love grows.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default SafetyCenter;
